import { Frequency } from '@core/referential/frequency.enum'
import { UnitType } from '@core/referential/unit-type.enum'
import { UsageType } from '@core/referential/usage-type.enum'
import { UseCase } from '@core/referential/use-case.enum'
import { AllowedFrequencies } from '@shared/schema/device.data-energy.schema'
import { createFileRoute, redirect } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { z } from 'zod'

import PendingComponent from '@/components/pending-component'
import { useAuth } from '@/hooks/use-auth'
import { parseDateRange } from '@/lib/date'
import { isUseCase } from '@/lib/utils'
import { companyDevicesQuery } from '@/queries/company.devices.query'

import { filterPhotovoltaicDevices, filterSites } from '../-utils/utils'

export const defaultDateRange = parseDateRange({
  startDate: dayjs().startOf('month').subtract(1, 'month').toDate(),
  endDate: dayjs().endOf('month').toDate(),
})

// La production photovoltaïque est toujours de l'énergie, en kWh ou MWh : contrairement à
// « Analyse par compteur », la page n'expose ni sélecteur d'énergie ni bascule Eau.
const productionUnits = [UnitType.KWH, UnitType.MWH]

export const SearchSchema = z.object({
  startDate: z.string().default(defaultDateRange.startDate),
  endDate: z.string().default(defaultDateRange.endDate),
  tab: z.literal('photovoltaic').default('photovoltaic'),
  frequency: AllowedFrequencies.default(Frequency.MONTHLY),
  site: z.string().optional(),
  devices: z.array(z.string()).optional(),
})

export type Search = z.infer<typeof SearchSchema>

export const Route = createFileRoute('/_dashboard/energy-usage/production/')({
  validateSearch: SearchSchema,
  async beforeLoad({ search, context: { queryClient } }) {
    if (!isUseCase(UseCase.CONSUMPTIONS)) {
      throw redirect({
        to: '/403',
        search: { reason: 'wrongUseCase' },
      })
    }

    const { setCurrentUsageType, setCurrentUnitType, setAvailableUnits, user } = useAuth.getState()

    // La barre du haut partage l'unité entre toutes les pages `/energy-usage`. Un
    // utilisateur arrivant depuis une page en m³ (Eau) tracerait sinon des kWh de
    // production étiquetés en m³.
    setCurrentUsageType(UsageType.ENERGY)
    setAvailableUnits({ [UsageType.ENERGY]: productionUnits })

    if (!user?.currentUnitType || !productionUnits.includes(user.currentUnitType)) {
      setCurrentUnitType(UnitType.KWH)
    }

    const companyDevices = await queryClient.ensureQueryData(companyDevicesQuery())

    if (companyDevices.length === 0) {
      return
    }

    const availableDevices = filterPhotovoltaicDevices({ devices: companyDevices })
    const availableSites = filterSites({ devices: availableDevices })
    const site = availableSites.some(({ id }) => id === search.site) ? search.site : availableSites[0]?.id

    if (!search.site && site) {
      throw redirect({
        to: '/energy-usage/production',
        search: { ...search, site },
        throw: false,
      })
    }
  },
  pendingComponent: PendingComponent,
})
